// DEPLOY: /var/www/woody.wtf/frontend/src/components/Navbar.js
// Shared site navbar used by every page. Logo links to https://woody.wtf. Alert bell + inbox (AlertBell) for signed-in users.
import React from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import AlertBell from './AlertBell';

/**
 * Shared site navbar.
 *
 * Props:
 *   backTo       {{ label, href }}  optional back link rendered next to the logo (e.g. "← Admin")
 *   children     optional center content (e.g. StrategyBuilder's picker button)
 *   compact      reduced horizontal padding for full-height app pages
 *   hideNavLinks hide the right-hand side entirely
 *   rightLinks   [{ label, href, muted }] extra links before the Dashboard link
 *   hideUser     hide the Dashboard link and username, keep Sign out (mobile app pages)
 */
export default function Navbar({ backTo, children, compact = false, hideNavLinks = false, rightLinks = [], hideUser = false }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  const onDashboard = location.pathname === '/dashboard';

  const s = {
    nav: {
      background: 'var(--surface)',
      borderBottom: '1px solid var(--border)',
      padding: compact ? '0 14px' : '0 32px',
      height: '56px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      flexShrink: 0,
    },
    left: { display: 'flex', alignItems: 'center', gap: '16px' },
    logo: {
      fontFamily: "'DM Mono', monospace",
      fontSize: '13px',
      fontWeight: 500,
      letterSpacing: '0.1em',
      color: 'var(--accent)',
      textTransform: 'uppercase',
      textDecoration: 'none',
    },
    navRight: {
      display: 'flex',
      alignItems: 'center',
      gap: '16px',
      fontSize: '13px',
      color: 'var(--text-muted)',
    },
    navLink: { color: 'var(--accent)', fontSize: '13px', textDecoration: 'none' },
    navLinkMuted: { color: 'var(--text-muted)', fontSize: '13px', textDecoration: 'none' },
    logoutBtn: {
      background: 'none',
      border: 'none',
      cursor: 'pointer',
      color: 'var(--accent)',
      fontSize: '13px',
      fontFamily: "'DM Sans', sans-serif",
      padding: '6px 12px',
      borderRadius: 'var(--radius)',
    },
  };

  return (
    <nav style={s.nav}>
      <div style={s.left}>
        <a
          href="https://woody.wtf"
          style={s.logo}
          onMouseEnter={e => { e.currentTarget.style.opacity = '0.7'; }}
          onMouseLeave={e => { e.currentTarget.style.opacity = '1'; }}
        >
          woody.wtf
        </a>
        {backTo && <Link to={backTo.href} style={s.navLink}>{backTo.label}</Link>}
      </div>
      {children}
      {!hideNavLinks && (
        <div style={s.navRight}>
          {rightLinks.map(l => (
            <a key={l.href} href={l.href} style={l.muted ? s.navLinkMuted : s.navLink}>{l.label}</a>
          ))}
          {!hideUser && <Link to="/help" style={s.navLinkMuted}>Help</Link>}
          {!onDashboard && !hideUser && <Link to="/dashboard" style={s.navLink}>Dashboard</Link>}
          {user && <AlertBell />}
          {!hideUser && <span>{user?.username}</span>}
          <button style={s.logoutBtn} onClick={handleLogout}>Sign out</button>
        </div>
      )}
    </nav>
  );
}
